import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UploadCloud, Check, ShieldCheck, Link2Off, FileSpreadsheet, Sparkles, RotateCcw, Landmark } from '../../lib/icons';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import {
  fetchFinances,
  fetchRapprochements,
  transactionsErp,
  suggererCorrespondance,
  pointerOperation,
  depointerOperation,
  soldeRapproche,
  analyserReleveComplet,
  justifierLigneBanque,
  classerLignesBanque,
  creerFraisBancaire,
  fetchInitialisationCampagne,
  annulerInitialisationCampagne,
  fetchIgnores,
  CAT_EXERCICE_ANTERIEUR,
} from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { couleurGain } from '../../lib/couleurs';
import { useAuth } from '../../lib/auth';
import { deposerDocument } from '../../lib/docs';
import { InitialisationCampagne } from './InitialisationCampagne';

const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';
const inputCls =
  'h-10 w-full border border-slate-300 bg-slate-50 px-2 font-data text-sm text-slate-900 outline-none focus:border-slate-900 focus:bg-white';

const cle = (obj, candidats) =>
  Object.keys(obj).find((k) => candidats.some((c) => k.toLowerCase().replace(/[^a-z]/g, '').includes(c)));

// Normalisation d'une ligne de relevé quel que soit l'intitulé des colonnes.
const lireLignes = (rows) =>
  rows
    .map((r, i) => {
      const kDate = cle(r, ['date', 'jour', 'valeur']);
      const kLib = cle(r, ['libelle', 'label', 'description', 'operation', 'nature']);
      const kMt = cle(r, ['montant', 'amount', 'credit', 'debit', 'somme']);
      const brut = r[kDate];
      let date = '';
      if (brut instanceof Date) date = brut.toISOString().slice(0, 10);
      else if (typeof brut === 'number') date = new Date(Date.UTC(1899, 11, 30 + brut)).toISOString().slice(0, 10);
      else if (brut) {
        const t = String(brut).trim();
        const m = t.match(/^(\d{2})[/\-.](\d{2})[/\-.](\d{4})$/);
        date = m ? `${m[3]}-${m[2]}-${m[1]}` : t.slice(0, 10);
      }
      const montant = Number(String(r[kMt] ?? '').replace(/\s/g, '').replace(',', '.')) || 0;
      return { id: `L${i + 1}`, date, libelle: String(r[kLib] ?? '—').trim(), montant };
    })
    .filter((l) => l.date && l.montant !== 0);

export const RapprochementBancaire = () => {
  const { user } = useAuth();
  const [fin, setFin] = useState(null);
  const [compteId, setCompteId] = useState('att');
  const [lignes, setLignes] = useState([]);
  const [choix, setChoix] = useState({});
  const [pointes, setPointes] = useState([]);
  const [survol, setSurvol] = useState(false);
  const [fichier, setFichier] = useState('');
  const [ia, setIa] = useState(null);
  const [source, setSource] = useState('');
  const [soldeReleve, setSoldeReleve] = useState(null);
  const [soldeInitialReleve, setSoldeInitialReleve] = useState(null);
  const [frais, setFrais] = useState({});
  const [classement, setClassement] = useState('');
  const [initOuvert, setInitOuvert] = useState(false);
  const [init, setInit] = useState(null);
  const [ignores, setIgnores] = useState([]);
  const input = useRef(null);

  const load = useCallback(async () => {
    setFin(await fetchFinances());
    setPointes(await fetchRapprochements());
    setInit(await fetchInitialisationCampagne());
    setIgnores(await fetchIgnores());
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  // `pointes` est volontairement dans les dépendances : le pointage d'une ligne
  // doit recalculer la liste des transactions ERP candidates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const candidats = useMemo(() => (fin ? transactionsErp(compteId) : []), [fin, compteId, pointes]);

  const suggestions = useMemo(
    () => Object.fromEntries(lignes.map((l) => [l.id, suggererCorrespondance(l, candidats)])),
    [lignes, candidats]
  );

  // Lignes sans écriture ERP : l'IA repère les prélèvements de la banque (frais, agios, TVA/IMP)
  // et propose de créer la dépense correspondante.
  useEffect(() => {
    const orphelines = lignes.filter((l) => !suggestions[l.id]);
    if (orphelines.length === 0) return setFrais({});
    let vivant = true;
    setClassement(`Analyse IA de ${orphelines.length} ligne(s) sans correspondance…`);
    classerLignesBanque(orphelines).then(({ classements, mode }) => {
      if (!vivant) return;
      setClassement(classements?.length ? `${classements.length} frais bancaire(s) détecté(s) — ${mode === 'ia' ? 'analyse IA' : 'repli mots-clés'}` : '');
      setFrais(Object.fromEntries((classements ?? []).map((c) => [c.id, c])));
    });
    return () => {
      vivant = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lignes]);

  const genererFrais = async (ligne) => {
    const c = frais[ligne.id];
    if (!c) return;
    const { error } = await creerFraisBancaire({
      compteId,
      ligne,
      categorie: c.categorie,
      libelle: c.libelle,
      utilisateur: user?.name,
    });
    if (error) return toast.error(error);
    toast.success(`Dépense « ${c.categorie} » créée et ligne pointée`);
    setLignes((ls) => ls.filter((l) => l.id !== ligne.id));
    load();
  };

  const genererTousFrais = async () => {
    const cibles = lignes.filter((l) => frais[l.id]);
    if (cibles.length === 0) return toast.error('Aucun frais bancaire détecté sur ce relevé.');
    let ok = 0;
    for (const l of cibles) {
      const c = frais[l.id];
      const { error } = await creerFraisBancaire({ compteId, ligne: l, categorie: c.categorie, libelle: c.libelle, utilisateur: user?.name });
      if (!error) ok += 1;
    }
    toast.success(`${ok} dépense(s) de frais bancaires créée(s) et pointée(s)`);
    setLignes((ls) => ls.filter((l) => !cibles.includes(l)));
    load();
  };

  const ignorerAvant = (date) => {
    const avant = lignes.filter((l) => l.date < date);
    if (avant.length === 0) return;
    setLignes((ls) => ls.filter((l) => l.date >= date));
    toast.success(`${avant.length} ligne(s) du relevé antérieure(s) au ${date} écartée(s) (exercice antérieur)`);
  };

  // Tableur/CSV lus localement ; PDF, photo ou fichier non structuré confiés à l'IA (Gemini 3 Pro).
  const lire = async (file) => {
    if (!file) return;
    const nom = file.name.toLowerCase();
    const tableur = /\.(csv|xlsx|xlsm|xls|txt)$/.test(nom);
    if (tableur) {
      try {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { cellDates: true });
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
        const parsees = lireLignes(rows);
        if (parsees.length > 0) {
          setLignes(parsees);
          setChoix({});
          setFichier(file.name);
          setSource('tableur');
          setSoldeReleve(null);
          return toast.success(`${parsees.length} ligne(s) lue(s) depuis ${file.name}`);
        }
        toast.info('Colonnes non reconnues — lecture par l’IA en cours…');
      } catch {
        toast.info('Fichier non structuré — lecture par l’IA en cours…');
      }
    }
    await lireParIA(file);
  };

  const lireParIA = async (file) => {
    setIa(file.name);
    // Le relevé est archivé au coffre-fort documentaire avant analyse.
    deposerDocument(file, { source: 'releve_bancaire', categorie: 'Extrait bancaire', deposePar: user?.name, indexer: false });
    const { error, lignes: brutes, compte: entete } = await analyserReleveComplet(file);
    setIa(null);
    if (error) return toast.error(error);
    const parsees = (brutes ?? [])
      .map((l, i) => ({
        id: `IA${i + 1}`,
        date: String(l.date ?? '').slice(0, 10),
        libelle: String(l.libelle ?? '—').trim(),
        montant: Number(l.montant) || 0,
      }))
      .filter((l) => /^\d{4}-\d{2}-\d{2}$/.test(l.date) && l.montant !== 0);
    if (parsees.length === 0) return toast.error('L’IA n’a trouvé aucune opération exploitable dans ce document.');
    setLignes(parsees);
    setChoix({});
    setFichier(file.name);
    setSource('ia');
    setSoldeReleve(Number(entete?.soldeFinal) || null);
    setSoldeInitialReleve(Number.isFinite(Number(entete?.soldeInitial)) ? Number(entete.soldeInitial) : null);
    toast.success(`${parsees.length} opération(s) extraite(s) par l’IA — ${entete?.banque || 'relevé'}`);
  };

  const pointer = async (ligne) => {
    const refErp = choix[ligne.id] ?? suggestions[ligne.id]?.ref;
    const { error } = await pointerOperation({ compteId, ligne, refErp, utilisateur: user?.name });
    if (error) return toast.error(error);
    toast.success(`Ligne pointée — solde réel de la banque mis à jour`);
    setLignes((ls) => ls.filter((l) => l.id !== ligne.id));
    load();
  };

  const pointerTout = async () => {
    const certaines = lignes.filter((l) => !choix[l.id] && (suggestions[l.id]?.score ?? 0) >= 85);
    if (certaines.length === 0) return toast.error('Aucune correspondance certaine à pointer.');
    for (const l of certaines) {
      await pointerOperation({ compteId, ligne: l, refErp: suggestions[l.id].ref, utilisateur: user?.name });
    }
    toast.success(`${certaines.length} ligne(s) pointée(s)`);
    setLignes((ls) => ls.filter((l) => !certaines.includes(l)));
    load();
  };

  if (!fin) return null;
  const compte = fin.comptes.find((c) => c.id === compteId);
  const banques = fin.comptes.filter((c) => c.type === 'Banque');
  const theorique = fin.soldes.find((c) => c.id === compteId)?.solde ?? 0;
  const reel = soldeRapproche(compte, fin);
  const pointesCompte = pointes.filter((p) => p.compteId === compteId);

  return (
    <div data-testid="rapprochement-bancaire" className="space-y-6">
      <div className="border border-slate-200 border-l-2 border-l-sky-600 bg-white px-4 py-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="flex items-center gap-2 font-heading text-base font-bold tracking-tight text-slate-900">
              <ShieldCheck size={16} className="text-sky-700" /> Import sécurisé du relevé
            </p>
            <p className="text-xs text-slate-500">
              Aucune connexion automatique à la banque : téléchargez votre relevé depuis l'espace BIAT / Attijari,
              puis déposez le fichier ici. Vos identifiants bancaires ne sont jamais demandés.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <button
              type="button"
              data-testid="ouvrir-initialisation"
              onClick={() => setInitOuvert(true)}
              className="flex h-10 items-center gap-2 border-2 border-sky-700 bg-sky-50 px-3 font-heading text-xs font-bold uppercase tracking-[0.06em] text-sky-800 hover:bg-sky-700 hover:text-white"
            >
              <RotateCcw size={14} /> Initialiser la campagne
            </button>
            {init && (
              <button
                type="button"
                data-testid="annuler-initialisation"
                onClick={async () => {
                  const { error } = await annulerInitialisationCampagne({ utilisateur: user?.name });
                  if (error) return toast.error(error);
                  toast.success('Initialisation annulée — soldes d’ouverture et écritures restaurés');
                  load();
                }}
                className="h-10 border border-slate-300 px-3 text-xs font-bold text-slate-600 hover:bg-slate-900 hover:text-white"
              >
                Annuler l’initialisation
              </button>
            )}
          </div>
        </div>
        {init && (
          <p data-testid="bandeau-initialisation" className="mt-3 flex flex-wrap items-center gap-2 border-l-4 border-emerald-600 bg-emerald-50 px-3 py-2 font-data text-[11px] text-emerald-900">
            <Landmark size={13} /> Campagne initialisée au {init.date} par {init.par} — {(init.refsIgnorees ?? []).length} écriture(s) en
            « Exercice antérieur / Ignoré » ({ignores.filter((x) => x.compteId === compteId).length} sur ce compte)
            <button
              type="button"
              data-testid="ignorer-lignes-anterieures"
              onClick={() => ignorerAvant(init.date)}
              className="ml-auto border border-emerald-300 bg-white px-2 py-1 font-bold text-emerald-800 hover:bg-emerald-700 hover:text-white"
            >
              Écarter les lignes du relevé antérieures au {init.date}
            </button>
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="border border-slate-200 bg-white px-4 py-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Compte bancaire</p>
          <select data-testid="rapp-compte" value={compteId} onChange={(e) => setCompteId(e.target.value)} className={`mt-1.5 ${inputCls}`}>
            {banques.map((c) => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
          <p className="mt-2 font-data text-[11px] text-slate-500">{compte?.rib}</p>
        </div>
        <div className="border border-slate-200 bg-white px-4 py-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Solde théorique (ERP)</p>
          <p data-testid="solde-theorique" className="tabular font-data text-2xl font-medium text-slate-900">{fmt(theorique, 3)}</p>
          <p className="font-data text-[11px] text-slate-500">TND — toutes écritures saisies</p>
        </div>
        <div className="border border-slate-200 border-l-2 border-l-emerald-600 bg-white px-4 py-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-emerald-700">Solde réel pointé</p>
          <p data-testid="solde-pointe" className="tabular font-data text-2xl font-medium text-slate-900">{fmt(reel, 3)}</p>
          <p data-testid="ecart-rapprochement" className={`font-data text-[11px] ${Math.abs(theorique - reel) <= 0.01 ? 'text-slate-500' : 'font-bold text-amber-800'}`}>
            Écart à rapprocher : {fmt(theorique - reel, 3)} TND
          </p>
        </div>
      </div>

      <div
        data-testid="zone-depot"
        onDragOver={(e) => {
          e.preventDefault();
          setSurvol(true);
        }}
        onDragLeave={() => setSurvol(false)}
        onDrop={(e) => {
          e.preventDefault();
          setSurvol(false);
          lire(e.dataTransfer.files?.[0]);
        }}
        onClick={() => !ia && input.current?.click()}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 border-2 border-dashed px-6 py-10 text-center transition-colors duration-150 ${
          ia ? 'border-violet-500 bg-violet-50' : survol ? 'border-slate-900 bg-slate-50' : 'border-slate-300 bg-white hover:bg-slate-50'
        }`}
      >
        {ia ? <Sparkles size={28} className="animate-pulse text-violet-700" /> : <UploadCloud size={28} className="text-slate-500" />}
        <p className="font-heading text-sm font-bold text-slate-900">
          {ia ? `Lecture IA du relevé — ${ia}` : 'Glissez-déposez le relevé bancaire (PDF, Excel, CSV, photo)'}
        </p>
        <p className="text-xs text-slate-500">
          {ia
            ? 'Gemini 3 Pro extrait les dates, libellés et montants — quelques secondes'
            : 'Aucun formatage nécessaire : les PDF et tableaux non standard sont analysés automatiquement par l’IA'}
        </p>
        {fichier && !ia && (
          <p data-testid="fichier-charge" className="mt-1 inline-flex items-center gap-1.5 border border-emerald-300 bg-emerald-50 px-2 py-1 font-data text-[11px] text-emerald-800">
            <FileSpreadsheet size={12} /> {fichier} — {lignes.length} ligne(s) à pointer
            {source === 'ia' && <span data-testid="badge-lecture-ia" className="ml-1 border border-violet-300 bg-violet-50 px-1 text-violet-800">lecture IA</span>}
          </p>
        )}
        <input
          ref={input}
          data-testid="input-releve"
          type="file"
          accept=".csv,.xlsx,.xls,.xlsm,.txt,.pdf,.jpg,.jpeg,.png,.webp"
          className="hidden"
          disabled={Boolean(ia)}
          onChange={(e) => lire(e.target.files?.[0])}
        />
      </div>

      {soldeReleve !== null && (
        <div data-testid="ecart-solde-releve" className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="border border-slate-200 bg-white px-4 py-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Solde de clôture du relevé</p>
            <p className="tabular font-data text-xl font-medium text-slate-900">{fmt(soldeReleve, 3)} TND</p>
          </div>
          <div className="border border-slate-200 bg-white px-4 py-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Solde théorique ERP</p>
            <p className="tabular font-data text-xl font-medium text-slate-900">{fmt(theorique, 3)} TND</p>
          </div>
          <div className={`border bg-white px-4 py-3 ${Math.abs(theorique - soldeReleve) <= 0.01 ? 'border-emerald-300 border-l-2 border-l-emerald-600' : 'border-amber-300 border-l-2 border-l-amber-600'}`}>
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Écart banque / ERP</p>
            <p className={`tabular font-data text-xl font-medium ${Math.abs(theorique - soldeReleve) <= 0.01 ? 'text-slate-900' : 'text-amber-800'}`}>{fmt(theorique - soldeReleve, 3)} TND</p>
            <p className="text-[11px] text-slate-500">
              {Math.abs(theorique - soldeReleve) <= 0.01 ? 'Comptes alignés' : 'À justifier par les lignes non pointées'}
            </p>
          </div>
        </div>
      )}

      {lignes.length > 0 && (
        <section className="border border-slate-200 bg-white">
          <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-heading text-base font-bold tracking-tight text-slate-900">Lettrage — relevé vs ERP</p>
              <p className="text-xs text-slate-500">La suggestion s'appuie sur le montant, la date et le n° de pièce / chèque</p>
              {classement && (
                <p data-testid="statut-classement-ia" className="mt-1 inline-flex items-center gap-1.5 border border-violet-300 bg-violet-50 px-2 py-0.5 font-data text-[11px] text-violet-800">
                  <Sparkles size={11} /> {classement}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.keys(frais).length > 0 && (
                <button
                  type="button"
                  data-testid="generer-tous-frais"
                  onClick={genererTousFrais}
                  className="flex h-10 items-center gap-2 border-2 border-violet-700 bg-violet-50 px-3 font-heading text-xs font-bold uppercase tracking-[0.06em] text-violet-800 hover:bg-violet-700 hover:text-white"
                >
                  <Sparkles size={14} /> Générer tous les frais bancaires ({Object.keys(frais).length})
                </button>
              )}
              <button
                type="button"
                data-testid="pointer-tout"
                onClick={pointerTout}
                className="flex h-10 items-center gap-2 border-2 border-slate-900 px-3 font-heading text-xs font-bold uppercase tracking-[0.06em] text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
              >
                <Check size={14} /> Tout pointer (correspondances certaines)
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left" data-testid="table-lettrage">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  {['Date banque', 'Libellé banque', 'Montant (TND)', 'Transaction ERP suggérée', 'Confiance', 'Action'].map((c) => (
                    <th key={c} className={th}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lignes.map((l) => {
                  const s = suggestions[l.id];
                  const ref = choix[l.id] ?? s?.ref ?? '';
                  return (
                    <tr key={l.id} data-testid={`ligne-banque-${l.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-data text-sm text-slate-700">{l.date}</td>
                      <td className="px-3 py-2 text-sm text-slate-800">{l.libelle}</td>
                      <td className={`tabular px-3 py-2 font-data text-sm font-semibold ${couleurGain(l.montant)}`}>{fmt(l.montant, 3)}</td>
                      <td className="px-3 py-2">
                        <select
                          data-testid={`choix-erp-${l.id}`}
                          value={ref}
                          onChange={(e) => setChoix((c) => ({ ...c, [l.id]: e.target.value }))}
                          className={inputCls}
                        >
                          <option value="">— Aucune correspondance —</option>
                          {candidats.map((t) => (
                            <option key={t.ref} value={t.ref}>
                              {`${t.date} · ${t.libelle} · ${t.montant < 0 ? '−' : '+'}${fmt(Math.abs(t.montant), 3)} TND`}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        {s ? (
                          <span
                            data-testid={`score-${l.id}`}
                            className={`font-data text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 ${
                              s.score >= 85 ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'
                            }`}
                          >
                            {s.score} %
                          </span>
                        ) : frais[l.id] ? (
                          <span data-testid={`frais-detecte-${l.id}`} className="inline-flex flex-col gap-0.5 border border-violet-300 bg-violet-50 px-1.5 py-0.5 font-data text-[10px] text-violet-900">
                            <span className="font-bold uppercase">Frais bancaire détecté</span>
                            <span>{frais[l.id].categorie}</span>
                            {frais[l.id].motif && <span className="text-violet-700">{frais[l.id].motif}</span>}
                          </span>
                        ) : (
                          <span data-testid={`sans-correspondance-${l.id}`} className="inline-flex items-center gap-1 font-data text-[10px] uppercase text-amber-800">
                            <Link2Off size={11} /> introuvable
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex justify-end gap-1.5">
                          {frais[l.id] && (
                            <button
                              type="button"
                              data-testid={`creer-frais-${l.id}`}
                              title={`Créer la dépense « ${frais[l.id].categorie} » et pointer la ligne`}
                              onClick={() => genererFrais(l)}
                              className="inline-flex items-center gap-1.5 border-2 border-violet-700 bg-violet-50 px-2 py-1 text-xs font-bold text-violet-800 hover:bg-violet-700 hover:text-white"
                            >
                              <Sparkles size={13} /> Créer la dépense
                            </button>
                          )}
                          <button
                            type="button"
                            data-testid={`pointer-${l.id}`}
                            disabled={!ref}
                            onClick={() => pointer(l)}
                            className="inline-flex items-center gap-1.5 border-2 border-slate-900 px-2 py-1 text-xs font-bold text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white disabled:border-slate-200 disabled:text-slate-300 disabled:hover:bg-transparent"
                          >
                            <Check size={13} /> Valider / Pointer
                          </button>
                          <button
                            type="button"
                            data-testid={`exercice-anterieur-${l.id}`}
                            title="Justifier en « Règlement exercice antérieur » (vieux chèque de la saison passée)"
                            onClick={async () => {
                              const { error } = await justifierLigneBanque({
                                compteId,
                                ligne: l,
                                categorie: CAT_EXERCICE_ANTERIEUR,
                                utilisateur: user?.name,
                              });
                              if (error) return toast.error(error);
                              toast.success('Ligne justifiée en « Règlement exercice antérieur » et pointée');
                              load();
                            }}
                            className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600 transition-colors duration-150 hover:bg-slate-700 hover:text-white"
                          >
                            Exercice antérieur
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="border border-slate-200 bg-white">
        <p className="border-b border-slate-200 px-4 py-3 font-heading text-base font-bold tracking-tight text-slate-900">
          Opérations pointées — {compte?.nom}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-pointees">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Date banque', 'Libellé relevé', 'Montant (TND)', 'Réf. ERP', 'Pointé par', ''].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pointesCompte.length === 0 ? (
                <tr data-testid="pointees-empty">
                  <td colSpan={6} className="px-3 py-10 text-center text-sm text-slate-500">
                    Aucune opération pointée : importez un relevé pour démarrer le rapprochement.
                  </td>
                </tr>
              ) : (
                pointesCompte.map((p) => (
                  <tr key={p.id} data-testid={`pointee-${p.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{p.dateBanque}</td>
                    <td className="px-3 py-2 text-sm text-slate-800">{p.libelleBanque}</td>
                    <td className={`tabular px-3 py-2 font-data text-sm font-semibold ${couleurGain(p.montantBanque)}`}>{fmt(p.montantBanque, 3)}</td>
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{p.refErp}</td>
                    <td className="px-3 py-2 text-sm text-slate-600">{p.utilisateur}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        data-testid={`depointer-${p.id}`}
                        onClick={async () => {
                          await depointerOperation(p.id);
                          toast.success('Pointage annulé');
                          load();
                        }}
                        className="border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
                      >
                        Dépointer
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {initOuvert && (
        <InitialisationCampagne
          compteReleveId={compteId}
          soldeReleveInitial={soldeInitialReleve}
          onClose={() => setInitOuvert(false)}
          onDone={(date) => {
            ignorerAvant(date);
            load();
          }}
        />
      )}
    </div>
  );
};
